<template>
  <div class="sys-maintenance-dashboard">
    <!-- Page Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h1 class="h3 mb-0">
          <i class="fas fa-cogs mr-2 text-warning"></i>
          System Maintenance
        </h1>
        <p class="text-muted mb-0">Manage users and clinics across the entire system</p>
      </div>
    </div>

    <!-- Tabbed Interface -->
    <b-tabs content-class="mt-3" v-model="activeTabIndex">
      
      <!-- Branch Management Tab (Moved to first) -->
      <b-tab title="Branch Management" active>
        <template #title>
          <i class="fas fa-building mr-2"></i>
          Branch Management
        </template>
        
        <div class="clinic-management-content">
          <!-- Clinic Action Buttons -->
          <div class="d-flex justify-content-end mb-3">
            <b-button-group>
              <b-button variant="outline-success" @click="refreshClinicSearch" :disabled="isClinicLoading">
                <i class="fas fa-sync-alt mr-1" :class="{ 'fa-spin': isClinicLoading }"></i>
                Refresh
              </b-button>
              <b-button variant="info" @click="exportAllClinics" :disabled="isClinicLoading || !hasClinicSearchResults">
                <i class="fas fa-download mr-1"></i>
                Export All
              </b-button>
              <b-button variant="warning" @click="generateReport" :disabled="isClinicLoading || !hasClinicSearchResults">
                <i class="fas fa-chart-bar mr-1"></i>
                Generate Report
              </b-button>
            </b-button-group>
          </div>

          <!-- Clinic Search Form -->
          <ClinicSearchForm 
            :is-loading="isClinicLoading"
            :last-search-criteria="clinicSearchCriteria"
            :has-results="hasClinicSearchResults"
            @search="handleClinicSearch"
            @export="exportAllClinics"
            class="mb-4"
          />

          <!-- Clinic Results Table -->
          <SuperadminDataTable
            title="Branch Search Results"
            header-icon="fas fa-building"
            :items="clinicSearchResults"
            :fields="clinicTableFields"
            :total-items="clinicPagination.total"
            :current-page="clinicCurrentPage"
            :items-per-page="clinicItemsPerPage"
            :is-loading="isClinicLoading"
            :sort-by="clinicSortBy"
            :sort-desc="clinicSortDesc"
            :show-status-actions="false"
            :show-refund-action="true"
            @view-details="viewClinicDetails"
            @refund="startRefund"
            @export-item="exportClinic"
            @export-data="exportAllClinics"
            @refresh="refreshClinicSearch"
            @page-changed="onClinicPageChanged"
            @per-page-changed="onClinicPerPageChanged"
            @sort-changed="onClinicSortChanged"
          />
        </div>
      </b-tab>

      <!-- User Management Tab (Moved to second) -->
      <b-tab title="User Management">
        <template #title>
          <i class="fas fa-users-cog mr-2"></i>
          User Management
        </template>
        
        <div class="user-management-content">
          <!-- User Action Buttons -->
          <div class="d-flex justify-content-end mb-3">
            <b-button-group>
              <b-button variant="outline-primary" @click="refreshUserSearch" :disabled="isUserLoading">
                <i class="fas fa-sync-alt mr-1" :class="{ 'fa-spin': isUserLoading }"></i>
                Refresh
              </b-button>
              <b-button variant="info" @click="exportAllUsers" :disabled="isUserLoading || !hasUserSearchResults">
                <i class="fas fa-download mr-1"></i>
                Export All
              </b-button>
            </b-button-group>
          </div>

          <!-- User Search Form -->
          <UserSearchForm 
            :is-loading="isUserLoading"
            :last-search-criteria="userSearchCriteria"
            @search="handleUserSearch"
            class="mb-4"
          />

          <!-- User Results Table -->
          <SuperadminDataTable
            title="User Search Results"
            header-icon="fas fa-users"
            :items="userSearchResults"
            :fields="userTableFields"
            :total-items="userPagination.total"
            :current-page="userCurrentPage"
            :items-per-page="userItemsPerPage"
            :is-loading="isUserLoading"
            :sort-by="userSortBy"
            :sort-desc="userSortDesc"
            :show-status-actions="true"
            @view-details="viewUserDetails"
            @toggle-status="toggleUserStatus"
            @export-item="exportUser"
            @export-data="exportAllUsers"
            @refresh="refreshUserSearch"
            @page-changed="onUserPageChanged"
            @per-page-changed="onUserPerPageChanged"
            @sort-changed="onUserSortChanged"
          />
        </div>
      </b-tab>

      <!-- Ads Management Tab -->
      <b-tab title="Ads Management">
        <template #title>
          <i class="fas fa-bullhorn mr-2"></i>
          Ads Management
        </template>
        
        <div class="ads-management-content">
          <!-- Ad content lives in MySQL now; this is where it is authored. -->
          <AdsManagerTable :stats="reportRows" @changed="onAdsChanged" />

          <!-- Engagement figures for the advertiser report. Feeds the per-row
               stats above, so it is loaded even when collapsed. -->
          <AdsReportPanel ref="adsReport" @loaded="reportRows = $event" />

          <!-- Ads Version Control Card -->
          <b-card title="Ads Version Control" header-bg-variant="warning" header-text-variant="white" class="mb-4">
            <b-card-text>
              <p class="mb-3">
                <i class="fas fa-info-circle text-info mr-2"></i>
                Force update advertisements across all devices by incrementing the version number. This will trigger apps to refresh their cached ad content.
              </p>
            </b-card-text>

            <!-- Current Version Display -->
            <div v-if="adsVersionInfo" class="version-info-section mb-4">
              <h5 class="text-primary mb-3">
                <i class="fas fa-chart-line mr-2"></i>
                Current Version Information
              </h5>
              <b-row>
                <b-col md="6">
                  <b-card bg-variant="light" class="mb-3">
                    <dl class="mb-0">
                      <dt class="text-primary">Current Version:</dt>
                      <dd class="h3 text-success mb-2">{{ adsVersionInfo.newValue }}</dd>
                      
                      <dt>Previous Version:</dt>
                      <dd class="text-muted">{{ adsVersionInfo.previousValue || '-' }}</dd>
                      
                      <dt>Last Updated:</dt>
                      <dd>{{ formatDateTime(adsVersionInfo.lastUpdated) }}</dd>
                      
                      <dt>Updated By:</dt>
                      <dd class="text-info">{{ adsVersionInfo.updatedBy }}</dd>
                    </dl>
                  </b-card>
                </b-col>
                <b-col md="6">
                  <div class="version-change-display">
                    <h6 class="text-secondary mb-2">Version Change</h6>
                    <div class="d-flex align-items-center justify-content-center py-3">
                      <span class="badge badge-secondary badge-lg mr-2">{{ adsVersionInfo.previousValue }}</span>
                      <i class="fas fa-arrow-right mx-3 text-primary"></i>
                      <span class="badge badge-success badge-lg">{{ adsVersionInfo.newValue }}</span>
                    </div>
                  </div>
                </b-col>
              </b-row>
            </div>

            <!-- Success Alert -->
            <b-alert 
              v-if="adsVersionSuccess" 
              variant="success" 
              show 
              dismissible
              @dismissed="adsVersionSuccess = null"
              class="mb-3"
            >
              <h5 class="alert-heading">
                <i class="fas fa-check-circle mr-2"></i>
                Ads Version Updated Successfully!
              </h5>
              <p class="mb-0">{{ adsVersionSuccess }}</p>
            </b-alert>

            <!-- Error Alert -->
            <b-alert 
              v-if="adsVersionError" 
              variant="danger" 
              show 
              dismissible
              @dismissed="adsVersionError = null"
              class="mb-3"
            >
              <h5 class="alert-heading">
                <i class="fas fa-exclamation-triangle mr-2"></i>
                Error
              </h5>
              <p class="mb-0">{{ adsVersionError }}</p>
            </b-alert>

            <!-- Action Section -->
            <div class="action-section">
              <h5 class="mb-3">
                <i class="fas fa-rocket mr-2"></i>
                Force Update Ads
              </h5>
              <p class="text-muted mb-3">
                Click the button below to increment the ads version. This action will:
              </p>
              <ul class="text-muted mb-4">
                <li>Increment the global ads version counter</li>
                <li>Force mobile apps to refresh advertisement content</li>
                <li>Clear cached ads on all devices</li>
                <li>Log the update with timestamp and user information</li>
              </ul>
              
              <b-button 
                variant="warning" 
                @click="incrementAdsVersion"
                :disabled="adsVersionLoading"
                size="lg"
                class="px-4"
              >
                <i class="fas fa-arrow-up mr-2" :class="{ 'fa-spin': adsVersionLoading }"></i>
                {{ adsVersionLoading ? 'Updating Ads Version...' : 'Force Update Ads' }}
              </b-button>
            </div>
          </b-card>

          <!-- Usage Instructions Card -->
          <b-card title="Usage Instructions" header-bg-variant="info" header-text-variant="white" class="mt-4">
            <b-card-text>
              <h6 class="text-primary">When to use this feature:</h6>
              <ul class="mb-3">
                <li><strong>New Ad Campaigns:</strong> When new advertisements have been uploaded to the system</li>
                <li><strong>Content Updates:</strong> When existing ad content has been modified</li>
                <li><strong>Emergency Updates:</strong> When ads need to be refreshed immediately</li>
                <li><strong>Cache Issues:</strong> When devices are showing outdated ad content</li>
              </ul>

              <h6 class="text-primary">What happens after clicking update:</h6>
              <ul class="mb-0">
                <li>Version number increments by 1</li>
                <li>All mobile apps will detect the version change</li>
                <li>Apps will automatically refresh their ad cache</li>
                <li>New ads will be displayed to users</li>
                <li>Change is logged with your user information</li>
              </ul>
            </b-card-text>
          </b-card>
        </div>
      </b-tab>

      <!-- Create Clinic/Branch/Import Tab -->
      <b-tab title="Create Clinic/Branch">
        <template #title>
          <i class="fas fa-plus-circle mr-2"></i>
          Create Clinic/Branch
        </template>
        <div class="create-clinic-branch-content">
          <b-row>
            <b-col md="4">
              <CreateClinicForm @success="handleCreateSuccess" />
            </b-col>
            <b-col md="4">
              <CreateBranchForm @success="handleCreateSuccess" />
            </b-col>
            <b-col md="4">
              <BulkPatientImportForm @success="handleImportSuccess" />
            </b-col>
          </b-row>
          <b-row class="mt-3">
            <b-col md="4">
              <BulkTreatmentImportForm @success="handleTreatmentImportSuccess" />
            </b-col>
            <b-col md="8">
              <LegacyBundleImportForm @running="legacyImportRunning = $event" />
            </b-col>
          </b-row>
        </div>
      </b-tab>

      <!-- Protocol Media Tab -->
      <!--
        lazy: หน้านี้ยิง API เฉพาะเมื่อกรอกเลข subscription อยู่แล้ว แต่ lazy กันไม่ให้
        ฟอร์มเรื่องเงินถูก mount ค้างไว้ทุกครั้งที่มีคนเปิดหน้า sys-maint
      -->
      <b-tab title="Billing Refund" lazy>
        <BillingRefundPanel :branch="refundBranch" @cleared="refundBranch = null" />
      </b-tab>

      <b-tab title="Protocol Media" lazy>
        <template #title>
          <i class="fas fa-images mr-2"></i>
          Protocol Media
        </template>
        <ProtocolMediaGallery />
      </b-tab>

      <!-- Admin MCP Tab: cross-clinic AI connector (appended last so existing tab indexes stay put) -->
      <b-tab title="MCP Admin" lazy>
        <template #title>
          <i class="fas fa-robot mr-2"></i>
          MCP Admin
        </template>
        <McpAdminPanel />
      </b-tab>
    </b-tabs>

    <!-- Create Result Modal -->
    <CreateResultModal ref="createResultModal" />

    <!-- Import Result Modal -->
    <ImportResultModal ref="importResultModal" />

    <!-- Treatment Import Result Modal -->
    <TreatmentImportResultModal ref="treatmentImportResultModal" />

    <!-- User Detail Modal -->
    <UserDetailModal ref="userDetailModal" />

    <!-- Clinic Detail Modal -->
    <ClinicDetailModal ref="clinicDetailModal" />

    <!-- User Status Confirmation Modal -->
    <b-modal
      id="user-status-confirm-modal"
      :title="userStatusModalTitle"
      @ok="confirmUserStatusChange"
      @cancel="cancelUserStatusChange"
    >
      <p>{{ userStatusModalMessage }}</p>
      
      <b-form-group label="Reason for status change:" label-for="user-status-reason">
        <b-form-textarea
          id="user-status-reason"
          v-model="userStatusChangeReason"
          placeholder="Enter reason for this status change..."
          rows="3"
          max-rows="5"
        ></b-form-textarea>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import UserSearchForm from '@/components/superadmin/UserSearchForm.vue';
import ClinicSearchForm from '@/components/superadmin/ClinicSearchForm.vue';
import SuperadminDataTable from '@/components/superadmin/SuperadminDataTable.vue';
import UserDetailModal from '@/components/superadmin/UserDetailModal.vue';
import ClinicDetailModal from '@/components/superadmin/ClinicDetailModal.vue';
import CreateClinicForm from '@/components/superadmin/CreateClinicForm.vue';
import CreateBranchForm from '@/components/superadmin/CreateBranchForm.vue';
import CreateResultModal from '@/components/superadmin/CreateResultModal.vue';
import BulkPatientImportForm from '@/components/superadmin/BulkPatientImportForm.vue';
import ImportResultModal from '@/components/superadmin/ImportResultModal.vue';
import BulkTreatmentImportForm from '@/components/superadmin/BulkTreatmentImportForm.vue';
import TreatmentImportResultModal from '@/components/superadmin/TreatmentImportResultModal.vue';
import LegacyBundleImportForm from '@/components/superadmin/LegacyBundleImportForm.vue';
import AdsManagerTable from '@/components/superadmin/AdsManagerTable.vue';
import BillingRefundPanel from '@/components/superadmin/BillingRefundPanel.vue';

// ลำดับของแท็บ Billing Refund ใน <b-tabs> ด้านบน (นับจาก 0)
// b-tabs อ้างถึงแท็บด้วยดัชนีเท่านั้น ไม่มีชื่อให้อ้าง — **ย้ายหรือแทรกแท็บเมื่อไรต้องแก้เลขนี้**
const BILLING_REFUND_TAB_INDEX = 4;
import AdsReportPanel from '@/components/superadmin/AdsReportPanel.vue';
import ProtocolMediaGallery from '@/components/superadmin/ProtocolMediaGallery.vue';
import McpAdminPanel from '@/components/superadmin/McpAdminPanel.vue';
import SuperadminService from '@/services/SuperadminService';

export default {
  name: "SysMaintenanceDashboard",
  components: {
    UserSearchForm,
    ClinicSearchForm,
    SuperadminDataTable,
    UserDetailModal,
    ClinicDetailModal,
    CreateClinicForm,
    CreateBranchForm,
    CreateResultModal,
    BulkPatientImportForm,
    ImportResultModal,
    BulkTreatmentImportForm,
    TreatmentImportResultModal,
    LegacyBundleImportForm,
    AdsManagerTable,
    BillingRefundPanel,
    AdsReportPanel,
    ProtocolMediaGallery,
    McpAdminPanel
  },
  // A legacy import keeps writing while this page is open; warn before leaving mid-run.
  beforeRouteLeave(to, from, next) {
    if (this.legacyImportRunning && !window.confirm('An import is still running. Leave this page and stop it?')) {
      next(false);
      return;
    }
    next();
  },
  data() {
    return {
      activeTabIndex: 0,
      legacyImportRunning: false,
      // สาขาที่ถูกกด "คืนเงิน" มาจากตารางค้นหา — ส่งต่อให้แท็บ Billing Refund
      refundBranch: null,

      // Engagement rows for the last 30 days, shared with the ads table so each
      // ad shows how it is actually performing.
      reportRows: [],

      // User Management Data
      userCurrentPage: 1,
      userItemsPerPage: 20,
      userSortBy: 'creation_dt',
      userSortDesc: true,
      userLastSearchParams: {},
      selectedUser: null,
      userStatusChangeReason: '',
      
      // Clinic Management Data
      clinicCurrentPage: 1,
      clinicItemsPerPage: 20,
      clinicSortBy: 'creation_dt', 
      clinicSortDesc: true,
      clinicLastSearchParams: {},
      
      // Ads Version Management Data
      adsVersionInfo: null,
      adsVersionLoading: false,
      adsVersionSuccess: null,
      adsVersionError: null,

      // Create Clinic/Branch Data
      lastCreationResult: null,
      
      // User Table Fields
      userTableFields: [
        {
          key: 'firstNameTh',
          label: 'Name (Thai)',
          sortable: true,
          formatter: (value, key, item) => {
            return `${item.firstNameTh || ''} ${item.lastNameTh || ''}`.trim();
          }
        },
        {
          key: 'firstNameEn',
          label: 'Name (English)',
          sortable: true,
          formatter: (value, key, item) => {
            return `${item.firstNameEn || ''} ${item.lastNameEn || ''}`.trim() || '-';
          }
        },
        {
          key: 'email',
          label: 'Email',
          sortable: true
        },
        {
          key: 'mobile',
          label: 'Mobile',
          sortable: false,
          formatter: (value) => value || '-'
        },
        {
          key: 'userTypeId',
          label: 'Type',
          sortable: true,
          slot: 'userType'
        },
        {
          key: 'drLicenseNo',
          label: 'License',
          sortable: true,
          formatter: (value) => value || '-'
        },
        {
          key: 'statusId',
          label: 'Status',
          sortable: true,
          slot: 'status'
        },
        {
          key: 'totalBranches',
          label: 'Branches',
          sortable: false,
          slot: 'totalBranches',
          formatter: (value) => value || 0
        },
        {
          key: 'creationDt',
          label: 'Created',
          sortable: true,
          slot: 'creationDt'
        },
        {
          key: 'actions',
          label: 'Actions',
          sortable: false,
          slot: 'actions',
          tdClass: 'text-center'
        }
      ],
      
      // Branch Table Fields (displaying individual branches instead of clinics)
      clinicTableFields: [
        {
          key: 'clinicNameTh',
          label: 'Clinic',
          sortable: true,
          tdClass: 'font-weight-medium'
        },
        {
          key: 'branchCode',
          label: 'Branch Code',
          sortable: true,
          tdClass: 'font-monospace'
        },
        {
          key: 'branchNameTh',
          label: 'Branch Name (Thai)', 
          sortable: true,
          formatter: (value) => value || '-'
        },
        {
          key: 'branchNameEn',
          label: 'Branch Name (English)',
          sortable: true,
          formatter: (value) => value || '-'
        },
        {
          key: 'address',
          label: 'Address',
          sortable: false,
          formatter: (value) => {
            if (!value) return '-';
            return value.length > 40 ? value.substring(0, 40) + '...' : value;
          }
        },
        {
          key: 'tel',
          label: 'Phone',
          sortable: false,
          formatter: (value) => value || '-'
        },
        {
          key: 'plan',
          label: 'แพ็ก',
          sortable: false,
          tdClass: 'text-center'
        },
        {
          key: 'memberCount',
          label: 'Members',
          sortable: false,
          formatter: (value) => value || 0,
          tdClass: 'text-center'
        },
        {
          key: 'status',
          label: 'Status',
          sortable: true,
          slot: 'branchStatus'
        },
        {
          key: 'creationDt',
          label: 'Created',
          sortable: true,
          slot: 'creationDt'
        },
        {
          key: 'actions',
          label: 'Actions',
          sortable: false,
          slot: 'actions',
          tdClass: 'text-center'
        }
      ]
    };
  },
  computed: {
    ...mapGetters('moduleSuperadmin', [
      'getUserSearchResults',
      'isUserSearchLoading', 
      'getUserSearchPagination',
      'getUserSearchCriteria',
      'getClinicSearchResults',
      'isClinicSearchLoading',
      'getClinicSearchPagination', 
      'getClinicSearchCriteria',
      'hasError',
      'getLastError'
    ]),
    
    // User computed properties
    userSearchResults() {
      return this.getUserSearchResults.users || [];
    },
    userPagination() {
      return this.getUserSearchPagination || {
        total: 0,
        limit: 20,
        offset: 0,
        totalPages: 0
      };
    },
    userSearchCriteria() {
      return this.getUserSearchCriteria || {};
    },
    isUserLoading() {
      return this.isUserSearchLoading;
    },
    hasUserSearchResults() {
      return this.userSearchResults.length > 0;
    },
    userStatusModalTitle() {
      if (!this.selectedUser) return '';
      const action = this.selectedUser.statusId === 1 ? 'Deactivate' : 'Activate';
      return `${action} User`;
    },
    userStatusModalMessage() {
      if (!this.selectedUser) return '';
      const action = this.selectedUser.statusId === 1 ? 'deactivate' : 'activate';
      const name = `${this.selectedUser.firstNameTh} ${this.selectedUser.lastNameTh}`;
      return `Are you sure you want to ${action} ${name}?`;
    },
    
    // Clinic computed properties (now displaying branches)
    clinicSearchResults() {
      return this.getClinicSearchResults.branches || [];
    },
    clinicPagination() {
      return this.getClinicSearchPagination || {
        total: 0,
        limit: 20,
        offset: 0,
        totalPages: 0
      };
    },
    clinicSearchCriteria() {
      return this.getClinicSearchCriteria || {};
    },
    isClinicLoading() {
      return this.isClinicSearchLoading;
    },
    hasClinicSearchResults() {
      return this.clinicSearchResults.length > 0;
    }
  },
  async mounted() {
    // Initial data loading removed - users can manually search using forms
  },
  methods: {
    ...mapActions('moduleSuperadmin', [
      'searchUsers',
      'updateUserStatus',
      'clearUserSearch',
      'searchClinics',
      'clearClinicSearch'
    ]),
    
    /**
     * Refresh the figures after an ad is created, edited or retired, so the
     * stats column does not keep describing an ad that just changed.
     */
    onAdsChanged() {
      this.$refs.adsReport?.load();
    },

    // User Management Methods
    async handleUserSearch(searchParams) {
      this.userLastSearchParams = { ...searchParams };
      this.userCurrentPage = 1;
      await this.performUserSearch();
    },
    
    async performUserSearch() {
      const params = {
        ...this.userLastSearchParams,
        sortBy: this.userSortBy,
        sortOrder: this.userSortDesc ? 'DESC' : 'ASC',
        limit: this.userItemsPerPage,
        offset: (this.userCurrentPage - 1) * this.userItemsPerPage
      };
      
      try {
        await this.searchUsers(params);
      } catch (error) {
        console.error('User search failed:', error);
        this.$bvToast.toast('Failed to search users. Please try again.', {
          title: 'Search Error',
          variant: 'danger',
          autoHideDelay: 5000
        });
      }
    },
    
    async performInitialUserSearch() {
      const defaultParams = {
        sortBy: 'creation_dt',
        sortOrder: 'DESC',
        limit: 20,
        offset: 0
      };
      
      try {
        await this.searchUsers(defaultParams);
      } catch (error) {
        console.error('Initial user search failed:', error);
        this.$bvToast.toast('Failed to load initial user data.', {
          title: 'Loading Error',
          variant: 'warning',
          autoHideDelay: 5000
        });
      }
    },
    
    async refreshUserSearch() {
      if (Object.keys(this.userLastSearchParams).length > 0) {
        await this.performUserSearch();
      } else {
        await this.performInitialUserSearch();
      }
    },
    
    async onUserPageChanged(page) {
      this.userCurrentPage = page;
      await this.performUserSearch();
    },
    
    async onUserPerPageChanged(perPage) {
      this.userItemsPerPage = perPage;
      this.userCurrentPage = 1;
      await this.performUserSearch();
    },
    
    async onUserSortChanged({ sortBy, sortDesc }) {
      this.userSortBy = sortBy;
      this.userSortDesc = sortDesc;
      await this.performUserSearch();
    },
    
    viewUserDetails(user) {
      this.$refs.userDetailModal.show(user.uid);
    },
    
    toggleUserStatus(user) {
      this.selectedUser = user;
      this.userStatusChangeReason = '';
      this.$bvModal.show('user-status-confirm-modal');
    },
    
    async confirmUserStatusChange() {
      if (!this.selectedUser) return;
      
      const newStatus = this.selectedUser.statusId === 1 ? 0 : 1;
      const action = newStatus === 1 ? 'activate' : 'deactivate';
      
      try {
        await this.updateUserStatus({
          userId: this.selectedUser.uid,
          statusData: {
            statusId: newStatus,
            reason: this.userStatusChangeReason || `User ${action}d via system maintenance interface`
          }
        });
        
        this.$bvToast.toast(`User ${action}d successfully`, {
          title: 'Status Updated',
          variant: 'success',
          autoHideDelay: 3000
        });
        
        await this.refreshUserSearch();
        
      } catch (error) {
        console.error('User status update failed:', error);
        this.$bvToast.toast(`Failed to ${action} user. Please try again.`, {
          title: 'Update Error',
          variant: 'danger',
          autoHideDelay: 5000
        });
      }
      
      this.cancelUserStatusChange();
    },
    
    cancelUserStatusChange() {
      this.selectedUser = null;
      this.userStatusChangeReason = '';
    },
    
    exportUser(user) {
      const csvData = this.generateUserCSV([user]);
      this.downloadCSV(csvData, `user_${user.uid}_data.csv`);
    },
    
    exportAllUsers() {
      if (this.userSearchResults.length === 0) {
        this.$bvToast.toast('No user data to export', {
          title: 'Export Warning',
          variant: 'warning',
          autoHideDelay: 3000
        });
        return;
      }
      
      const csvData = this.generateUserCSV(this.userSearchResults);
      this.downloadCSV(csvData, 'users_export.csv');
    },
    
    generateUserCSV(users) {
      let csv = 'User ID,Name (Thai),Name (English),Email,Mobile,Type,License,Status,Branches,Created\\n';
      
      users.forEach(user => {
        const nameTh = `${user.firstNameTh || ''} ${user.lastNameTh || ''}`.trim();
        const nameEn = `${user.firstNameEn || ''} ${user.lastNameEn || ''}`.trim();
        const userType = user.userTypeId === 1 ? 'Doctor' : 'Staff';
        const status = user.statusId === 1 ? 'Active' : 'Inactive';
        const created = user.creationDt ? new Date(user.creationDt).toLocaleDateString() : '';
        
        csv += `${user.uid},"${nameTh}","${nameEn}",${user.email || ''},${user.mobile || ''},${userType},${user.drLicenseNo || ''},${status},${user.totalBranches || 0},${created}\\n`;
      });
      
      return csv;
    },
    
    // Clinic Management Methods
    async handleClinicSearch(searchParams) {
      this.clinicLastSearchParams = { ...searchParams };
      this.clinicCurrentPage = 1;
      await this.performClinicSearch();
    },
    
    async performClinicSearch() {
      const params = {
        ...this.clinicLastSearchParams,
        sortBy: this.clinicSortBy,
        sortOrder: this.clinicSortDesc ? 'DESC' : 'ASC',
        limit: this.clinicItemsPerPage,
        offset: (this.clinicCurrentPage - 1) * this.clinicItemsPerPage
      };
      
      try {
        await this.searchClinics(params);
      } catch (error) {
        console.error('Clinic search failed:', error);
        this.$bvToast.toast('Failed to search clinics. Please try again.', {
          title: 'Search Error',
          variant: 'danger',
          autoHideDelay: 5000
        });
      }
    },
    
    async performInitialClinicSearch() {
      const defaultParams = {
        sortBy: 'creation_dt',
        sortOrder: 'DESC',
        limit: 20,
        offset: 0
      };
      
      try {
        await this.searchClinics(defaultParams);
      } catch (error) {
        console.error('Initial clinic search failed:', error);
        this.$bvToast.toast('Failed to load initial clinic data.', {
          title: 'Loading Error',
          variant: 'warning',
          autoHideDelay: 5000
        });
      }
    },
    
    async refreshClinicSearch() {
      if (Object.keys(this.clinicLastSearchParams).length > 0) {
        await this.performClinicSearch();
      } else {
        await this.performInitialClinicSearch();
      }
    },
    
    async onClinicPageChanged(page) {
      this.clinicCurrentPage = page;
      await this.performClinicSearch();
    },
    
    async onClinicPerPageChanged(perPage) {
      this.clinicItemsPerPage = perPage;
      this.clinicCurrentPage = 1;
      await this.performClinicSearch();
    },
    
    async onClinicSortChanged({ sortBy, sortDesc }) {
      this.clinicSortBy = sortBy;
      this.clinicSortDesc = sortDesc;
      await this.performClinicSearch();
    },
    
    /**
     * กดคืนเงินจากแถวสาขา — พาไปแท็บ Billing Refund พร้อมสาขานั้น
     *
     * เลข subscription ไม่โผล่ที่ไหนให้ทีมเห็นเลย ทางเข้าที่ใช้ได้จริงจึงต้องเริ่มจาก
     * "ค้นชื่อคลินิก" ซึ่งเป็นสิ่งเดียวที่ทีมรู้ตอนคลินิกโทรมา
     */
    startRefund(branch) {
      this.refundBranch = branch;
      this.activeTabIndex = BILLING_REFUND_TAB_INDEX;
    },

    viewClinicDetails(branch) {
      // Pass entire branch object to modal with existing data
      this.$refs.clinicDetailModal.showBranch(branch);
    },
    
    exportClinic(branch) {
      const csvData = this.generateBranchCSV([branch]);
      this.downloadCSV(csvData, `branch_${branch.branchCode}_data.csv`);
    },
    
    exportAllClinics() {
      if (this.clinicSearchResults.length === 0) {
        this.$bvToast.toast('No branch data to export', {
          title: 'Export Warning',
          variant: 'warning',
          autoHideDelay: 3000
        });
        return;
      }
      
      const csvData = this.generateBranchCSV(this.clinicSearchResults);
      this.downloadCSV(csvData, 'branches_export.csv');
    },
    
    generateBranchCSV(branches) {
      let csv = 'Branch Code,Branch Name (Thai),Branch Name (English),Clinic Name,Address,Phone,Members,Status,Created\\n';
      
      branches.forEach(branch => {
        const status = branch.status === 1 ? 'Active' : 'Inactive';
        const created = branch.creationDt ? new Date(branch.creationDt).toLocaleDateString() : '';
        
        csv += `"${branch.branchCode || ''}","${branch.branchNameTh || ''}","${branch.branchNameEn || ''}","${branch.clinicNameTh || ''}","${branch.address || ''}","${branch.tel || ''}",${branch.memberCount || 0},"${status}","${created}"\\n`;
      });
      
      return csv;
    },
    
    generateReport() {
      this.$bvToast.toast('Report generation feature coming soon!', {
        title: 'Feature Notice',
        variant: 'info',
        autoHideDelay: 3000
      });
    },
    
    // Common Utility Methods
    downloadCSV(csvData, filename) {
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        this.$bvToast.toast(`Exported ${filename} successfully`, {
          title: 'Export Complete',
          variant: 'success',
          autoHideDelay: 3000
        });
      }
    },

    // Ads Version Management Methods
    async incrementAdsVersion() {
      this.adsVersionLoading = true;
      this.adsVersionError = null;
      this.adsVersionSuccess = null;

      try {
        const response = await SuperadminService.incrementAdsVersion();
        this.adsVersionInfo = response.data.data;
        
        const { previousValue, newValue } = this.adsVersionInfo;
        this.adsVersionSuccess = `Ads version successfully updated from ${previousValue} to ${newValue}. All devices will refresh their ad content automatically.`;
        
        this.$bvToast.toast(`Ads version updated to ${newValue}`, {
          title: 'Ads Force Update Complete',
          variant: 'success',
          autoHideDelay: 5000
        });
        
      } catch (error) {
        console.error('Error incrementing ads version:', error);
        
        if (error.response?.status === 403) {
          this.adsVersionError = 'Access denied. Superadmin privileges required.';
        } else if (error.response?.status === 400) {
          this.adsVersionError = 'Ads version configuration not found. Please contact system administrator.';
        } else if (error.response?.data?.message) {
          this.adsVersionError = error.response.data.message;
        } else {
          this.adsVersionError = 'Failed to update ads version. Please try again or contact support.';
        }
        
        this.$bvToast.toast(this.adsVersionError, {
          title: 'Ads Update Error',
          variant: 'danger',
          autoHideDelay: 7000
        });
      } finally {
        this.adsVersionLoading = false;
      }
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      try {
        return new Date(dateString).toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      } catch (error) {
        return dateString;
      }
    },

    // Create Clinic/Branch Methods
    handleCreateSuccess(result) {
      this.lastCreationResult = result;
      this.$refs.createResultModal.show(result);
    },

    handleImportSuccess(result) {
      this.$refs.importResultModal.show(result);
    },
    handleTreatmentImportSuccess(result) {
      this.$refs.treatmentImportResultModal.show(result);
    }
  },
  
  beforeDestroy() {
    // Clear search results when leaving the page
    this.clearUserSearch();
    this.clearClinicSearch();
  }
};
</script>

<style scoped>
.sys-maintenance-dashboard {
  padding: 1.5rem;
  max-width: 100%;
}

.sys-maintenance-dashboard h1 {
  color: #495057;
  font-weight: 600;
}

.sys-maintenance-dashboard .fa-spin {
  animation: fa-spin 2s infinite linear;
}

@keyframes fa-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Tab styling */
.sys-maintenance-dashboard .nav-tabs .nav-link {
  border-radius: 0.5rem 0.5rem 0 0;
  border: 1px solid #dee2e6;
  margin-right: 0.125rem;
}

.sys-maintenance-dashboard .nav-tabs .nav-link.active {
  background-color: #f8f9fa;
  border-bottom-color: #f8f9fa;
}

/* Content styling */
.user-management-content,
.clinic-management-content,
.create-clinic-branch-content {
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-radius: 0 0.5rem 0.5rem 0.5rem;
  border: 1px solid #dee2e6;
  border-top: none;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .sys-maintenance-dashboard {
    padding: 1rem;
  }
  
  .sys-maintenance-dashboard h1 {
    font-size: 1.5rem;
  }
  
  .sys-maintenance-dashboard .d-flex {
    flex-direction: column;
    align-items: flex-start !important;
  }
  
  .sys-maintenance-dashboard .btn-group {
    margin-top: 1rem;
    width: 100%;
  }
  
  .user-management-content,
  .clinic-management-content,
  .create-clinic-branch-content,
  .import-patients-content {
    padding: 1rem;
  }
}
</style>